import React, { useEffect, useRef, useState } from 'react';
import ChevronDownIcon from './ChevronDownIcon';

function optionValue(opt) {
  return typeof opt === 'string' ? opt : opt.value;
}

function optionLabel(opt) {
  return typeof opt === 'string' ? opt : opt.label;
}

/**
 * Select / dropdown atom.
 */
export default function Select({
  value,
  onChange,
  options = [],
  placeholder = 'Select',
  className = '',
  disabled = false,
  maxVisible,
  ...props
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const currentValue = value ?? '';
  const selected = options.find((opt) => String(optionValue(opt)) === String(currentValue));
  const selectedText = selected ? optionLabel(selected) : '';

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const emitChange = (nextValue) => {
    onChange?.({ target: { value: nextValue } });
  };

  if (!maxVisible) {
    return (
      <div className={`ca-select ${className}`.trim()}>
        <select
          className="ca-select__control"
          value={currentValue}
          onChange={onChange}
          disabled={disabled}
          {...props}
        >
          <option value="" disabled>
            {placeholder}
          </option>
          {options.map((opt) => {
            const val = optionValue(opt);
            return (
              <option key={val} value={val}>
                {optionLabel(opt)}
              </option>
            );
          })}
        </select>
        <span className="ca-select__chevron">
          <ChevronDownIcon size={16} />
        </span>
      </div>
    );
  }

  return (
    <div className={`ca-select ca-select--menu ${className}`.trim()} ref={rootRef}>
      <button
        type="button"
        id={props.id}
        className={`ca-select__control${selectedText ? '' : ' ca-select__control--placeholder'}`}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        {selectedText || placeholder}
      </button>
      <span className="ca-select__chevron">
        <ChevronDownIcon size={16} />
      </span>
      {open ? (
        <ul
          className="ca-select__menu"
          role="listbox"
          style={{ '--ca-select-max-visible': maxVisible }}
        >
          {options.map((opt) => {
            const val = optionValue(opt);
            const isSelected = String(val) === String(currentValue);
            return (
              <li key={val} role="none">
                <button
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={`ca-select__option${isSelected ? ' is-selected' : ''}`}
                  onClick={() => {
                    emitChange(val);
                    setOpen(false);
                  }}
                >
                  {optionLabel(opt)}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
